import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  ArrowLeftRight,
  Clock,
  Globe,
  Wrench,
  CreditCard,
  Code,
  BookOpen,
  Shield,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  Webhook,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import wrenchLogo from '../../assets/wrench.png';
import AppFooter from './AppFooter';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

/* eslint-disable sonarjs/cognitive-complexity --
   pre-existing in this Layout shell; will be addressed when the
   sidebar menu is broken into per-role subcomponents. Out of scope
   for #429 (logo asset swap). */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation('layout');
  const { logout, user, tenant } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isStaff = user?.role === 'staff';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    logout();
  };

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    },
    [mobileMenuOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div
      className={`layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'layout-mobile' : ''}`}
    >
      <a href="#main-content" className="skip-link">
        {t('skipLink')}
      </a>

      {/* Mobile top bar */}
      <header className="mobile-header">
        {/* Both brand links are named by their visible title alone (OA-24). An aria-label of
            "Office A11y home" over the visible "Office A11y" gave each link two competing names
            (STRUCTURE-23). When the sidebar collapses, Layout.css hides the title visually but
            keeps it in the accessibility tree, so the link never goes nameless. */}
        <Link to="/dashboard" className="brand-link">
          <img src={wrenchLogo} alt="" className="brand-mark" aria-hidden="true" />
          <span className="brand-title">{t('brand.title')}</span>
        </Link>
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          // aria-controls must reference an element that exists in the
          // DOM. The sidebar-nav is only mounted when the menu is open
          // (or on desktop), so only advertise the relationship when it's
          // actually present — avoids the "references id that doesn't
          // exist" validator failure.
          aria-controls={mobileMenuOpen || !isMobile ? 'sidebar-nav' : undefined}
          aria-label={mobileMenuOpen ? t('menu.close') : t('menu.open')}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Backdrop overlay for mobile */}
      {mobileMenuOpen && (
        <div
          className="mobile-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {(!isMobile || mobileMenuOpen) && (
        <nav
          className={`sidebar ${mobileMenuOpen ? 'sidebar-mobile-open' : ''}`}
          aria-label={t('nav.mainLabel')}
          id="sidebar-nav"
        >
          <div className="sidebar-header">
            <Link to="/dashboard" className="brand-link">
              <img src={wrenchLogo} alt="" className="brand-mark" aria-hidden="true" />
              <span className="brand-title">{t('brand.title')}</span>
            </Link>
            <button
              className="sidebar-toggle desktop-only"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              aria-label={sidebarCollapsed ? t('menu.expandSidebar') : t('menu.collapseSidebar')}
            >
              {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
            <button
              className="sidebar-toggle mobile-only"
              onClick={() => setMobileMenuOpen(false)}
              aria-label={t('menu.close')}
            >
              <X size={20} />
            </button>
          </div>

          <ul className="sidebar-menu">
            <li>
              <Link
                to="/dashboard"
                data-tooltip={t('nav.dashboard')}
                className={`sidebar-link ${isActive('/dashboard') ? 'active' : ''}`}
                aria-current={isActive('/dashboard') ? 'page' : undefined}
              >
                <LayoutDashboard className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.dashboard')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/analyze"
                data-tooltip={t('nav.analyze')}
                className={`sidebar-link ${isActive('/analyze') ? 'active' : ''}`}
                aria-current={isActive('/analyze') ? 'page' : undefined}
              >
                <Search className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.analyze')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/convert"
                data-tooltip={t('nav.convert')}
                className={`sidebar-link ${isActive('/convert') ? 'active' : ''}`}
                aria-current={isActive('/convert') ? 'page' : undefined}
              >
                <ArrowLeftRight className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.convert')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/history"
                data-tooltip={t('nav.history')}
                className={`sidebar-link ${isActive('/history') ? 'active' : ''}`}
                aria-current={isActive('/history') ? 'page' : undefined}
              >
                <Clock className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.history')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/discover"
                data-tooltip={t('nav.discover')}
                className={`sidebar-link ${isActive('/discover') ? 'active' : ''}`}
                aria-current={isActive('/discover') ? 'page' : undefined}
              >
                <Globe className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.discover')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/remediation"
                data-tooltip={t('nav.remediation')}
                className={`sidebar-link ${isActive('/remediation') ? 'active' : ''}`}
                aria-current={isActive('/remediation') ? 'page' : undefined}
              >
                <Wrench className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.remediation')}</span>
              </Link>
            </li>
            {tenant && (tenant.tenantRole === 'owner' || tenant.tenantRole === 'manager') && (
              <li>
                <Link
                  to="/organization"
                  data-tooltip={t('nav.organization')}
                  className={`sidebar-link ${isActive('/organization') ? 'active' : ''}`}
                  aria-current={isActive('/organization') ? 'page' : undefined}
                >
                  <Shield className="sidebar-icon" aria-hidden="true" />
                  <span className="sidebar-label">{t('nav.organization')}</span>
                </Link>
              </li>
            )}
            {!isAdmin && !isStaff && (
              <li>
                <Link
                  to="/billing"
                  data-tooltip={t('nav.billing')}
                  className={`sidebar-link ${isActive('/billing') ? 'active' : ''}`}
                  aria-current={isActive('/billing') ? 'page' : undefined}
                >
                  <CreditCard className="sidebar-icon" aria-hidden="true" />
                  <span className="sidebar-label">{t('nav.billing')}</span>
                </Link>
              </li>
            )}
            <li>
              <Link
                to="/how-to"
                data-tooltip={t('nav.howTo')}
                className={`sidebar-link ${isActive('/how-to') ? 'active' : ''}`}
                aria-current={isActive('/how-to') ? 'page' : undefined}
              >
                <BookOpen className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.howTo')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/developers"
                data-tooltip={t('nav.developers')}
                className={`sidebar-link ${isActive('/developers') ? 'active' : ''}`}
                aria-current={isActive('/developers') ? 'page' : undefined}
              >
                <Code className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.developers')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/webhooks"
                data-tooltip={t('nav.webhooks')}
                className={`sidebar-link ${isActive('/webhooks') ? 'active' : ''}`}
                aria-current={isActive('/webhooks') ? 'page' : undefined}
              >
                <Webhook className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-label">{t('nav.webhooks')}</span>
              </Link>
            </li>
            {(isAdmin || isStaff) && (
              <li>
                <Link
                  to="/admin"
                  data-tooltip={t('nav.admin')}
                  className={`sidebar-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                  aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
                >
                  <Shield className="sidebar-icon" aria-hidden="true" />
                  <span className="sidebar-label">{t('nav.admin')}</span>
                </Link>
              </li>
            )}
          </ul>

          {/* Account and Sign Out take their names from their visible labels, like every link
              above (OA-24). Their aria-labels ("Account settings", "Sign out of Office A11y")
              competed with that text (STRUCTURE-23). */}
          <div className="sidebar-footer" role="region" aria-label={t('userMenu.regionLabel')}>
            <Link
              to="/account"
              data-tooltip={t('userMenu.account')}
              className="sidebar-link account-link"
            >
              <User className="sidebar-icon" aria-hidden="true" />
              <span className="sidebar-label">{t('userMenu.account')}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="logout-button"
              data-tooltip={t('userMenu.signOut')}
            >
              <LogOut className="sidebar-icon" aria-hidden="true" />
              <span className="sidebar-label">{t('userMenu.signOut')}</span>
            </button>
          </div>
        </nav>
      )}

      {/* OA-375: <main> and the site footer are siblings in this column, not nested.
          AppFooter carries role="contentinfo"; rendering it inside <main> put one
          top-level landmark inside another (STRUCTURE-24) and made the legal links part
          of every page's main content. The sidebar offset lives on this wrapper now
          rather than on .main-content, so both children clear the fixed sidebar. */}
      <div className="layout-body" style={isMobile ? { marginLeft: 0 } : undefined}>
        {/* tabIndex -1 so the skip link's target can take focus, as AdminLayout's <main>
            already does. Without it the skip link set the hash and scrolled but left focus
            on <body>, so the next Tab returned to the top of the page and undid the jump
            (SC 2.4.1, measured in Chromium). ScrollToTop's temporary-tabindex workaround
            covers router hash navigation, not a plain in-page anchor like this one. */}
        <main className="main-content" id="main-content" tabIndex={-1}>
          <div className="content-container">{children}</div>
        </main>
        <AppFooter />
      </div>
    </div>
  );
};

export default Layout;
